import axios, { AxiosError } from 'axios';

// Resolve the API origin from env, falling back to the local dev host. Without a
// fallback, a missing VITE_API_URL silently produces `undefined/api/...` requests
// that 404 against the SPA dev server instead of hitting the backend.
export const API_ORIGIN = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';

// Sanctum SPA cookie auth: withCredentials + XSRF cookie/header. The API and
// SPA must share a top-level domain in production for the cookie to apply.
const api = axios.create({
  baseURL: `${API_ORIGIN}/api`,
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

let csrfInitialized = false;

/**
 * Prime the CSRF cookie before the first mutating request (Sanctum requirement).
 */
export async function ensureCsrf(): Promise<void> {
  if (csrfInitialized) return;
  await axios.get(`${API_ORIGIN}/sanctum/csrf-cookie`, {
    withCredentials: true,
  });
  csrfInitialized = true;
}

// Global 401 handling: a session that expires mid-session should land the user
// on a clean re-auth, not scatter generic "Something went wrong" toasts. The
// /user probe legitimately 401s for anonymous visitors browsing the public
// catalogue, and /login failures are handled inline - those are excluded.
api.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (error instanceof AxiosError && error.response?.status === 401) {
      const url = error.config?.url ?? '';
      const isAuthProbe = url.includes('/user') || url.includes('/login');
      if (!isAuthProbe && !window.location.pathname.startsWith('/login')) {
        csrfInitialized = false;
        // L3: carry the page they were on so login can return them there (e.g.
        // an expiry mid-checkout lands back on /checkout, not /account). This is
        // a full-page redirect, so the path rides as a query param, not router
        // state; LoginPage validates it is a same-origin path before using it.
        const from = window.location.pathname + window.location.search;
        window.location.assign(`/login?from=${encodeURIComponent(from)}`);
      }
    }
    return Promise.reject(error);
  },
);

/** Normalize an axios error into a human-readable message. */
export function apiError(err: unknown): string {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      return Object.values(data.errors).flat().join(' ');
    }
    if (data?.message) return data.message;
    if (err.message) return err.message;
  }
  return 'Something went wrong. Please try again.';
}

/**
 * Laravel's per-field validation bag, flattened to one message per field and
 * keyed exactly as sent (`dimensions.l`), so a form can map it onto its inputs.
 * Separate from apiError(), which joins the whole bag into a single string -
 * many call sites depend on that and must not change.
 */
export function apiFieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof AxiosError)) return {};
  const data = err.response?.data as { errors?: Record<string, string[]> } | undefined;
  if (!data?.errors) return {};

  return Object.fromEntries(
    Object.entries(data.errors)
      .map(([field, messages]) => [field, messages[0]])
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
  );
}

export default api;
